import React, { useLayoutEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import type { PaperQuestionState } from "../../PracticePapers/types"
import { pctOf, scoreToneClass } from "../utils/format"
import "./AssessmentCard.style.scss"

interface PaperQuestionPickerProps {
  questions: PaperQuestionState[]
  selected: number
  onSelect: (index: number) => void
}

const EDGE = 2

const PaperQuestionPicker = ({
  questions,
  selected,
  onSelect,
}: PaperQuestionPickerProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const revealed = useRef(false)
  const [edges, setEdges] = useState({ overflow: false, atStart: true, atEnd: true })

  const measure = () => {
    const el = scrollerRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setEdges({
      overflow: max > EDGE,
      atStart: el.scrollLeft <= EDGE,
      atEnd: el.scrollLeft >= max - EDGE,
    })
  }

  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    measure()
    el.addEventListener("scroll", measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      el.removeEventListener("scroll", measure)
      observer.disconnect()
    }
  }, [questions])

  useLayoutEffect(() => {
    const el = scrollerRef.current
    const chip = el?.querySelector<HTMLElement>(`[data-index="${selected}"]`)
    if (!el || !chip) return
    const pad = 8
    const view = el.getBoundingClientRect()
    const chipBox = chip.getBoundingClientRect()
    const behavior = revealed.current ? "smooth" : "auto"
    revealed.current = true
    if (chipBox.left < view.left + pad) {
      el.scrollBy({ left: chipBox.left - view.left - pad, behavior })
    } else if (chipBox.right > view.right - pad) {
      el.scrollBy({ left: chipBox.right - view.right + pad, behavior })
    }
  }, [selected, questions])

  const page = (direction: -1 | 1) => {
    const el = scrollerRef.current
    if (!el) return
    const chips = Array.from(el.querySelectorAll<HTMLElement>("[data-index]"))
    const max = el.scrollWidth - el.clientWidth
    const destination = Math.min(
      max,
      Math.max(0, el.scrollLeft + direction * Math.max(el.clientWidth - 48, 80))
    )
    const chip =
      direction > 0
        ? chips.find((item) => item.offsetLeft >= destination - 1)
        : [...chips].reverse().find((item) => item.offsetLeft <= destination + 1)
    el.scrollTo({
      left: destination >= max - EDGE ? max : (chip?.offsetLeft ?? destination),
      behavior: "smooth",
    })
  }

  return (
    <div className="paper-assessor__pick-row">
      {edges.overflow && (
        <button
          type="button"
          className="paper-assessor__pick-arrow"
          aria-label="Scroll questions left"
          disabled={edges.atStart}
          onClick={() => page(-1)}
        >
          <ChevronLeft size={16} />
        </button>
      )}
      <div
        ref={scrollerRef}
        className="paper-assessor__pick"
        role="tablist"
        aria-label="Questions"
      >
        {questions.map((question, index) => {
          const pct =
            question.score !== null ? pctOf(question.score, question.maxScore) : null
          return (
            <button
              key={`${question.questionId}-${index}`}
              type="button"
              role="tab"
              data-index={index}
              aria-selected={index === selected}
              className={`assessment-card__qchip paper-assessor__pick-btn ${
                index === selected ? "is-selected" : ""
              } ${pct == null ? "is-empty" : scoreToneClass(pct)}`}
              onClick={() => onSelect(index)}
            >
              Q{index + 1}
              {question.score !== null ? ` ${question.score}/${question.maxScore}` : ""}
            </button>
          )
        })}
      </div>
      {edges.overflow && (
        <button
          type="button"
          className="paper-assessor__pick-arrow"
          aria-label="Scroll questions right"
          disabled={edges.atEnd}
          onClick={() => page(1)}
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  )
}

export default PaperQuestionPicker
